import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import apiRequestCall from "../../../apiHelper/globalHelper";
import Search from "../../../formHelper/search";
import Pagination from "../../../formHelper/pagination";
import Table from "../../../formHelper/list";
import FilterDropdown from "../../../formHelper/filterDropDown";
import Loader from "../../../formHelper/Loader";
import Success from "../../../formHelper/success";
import Delete from "../../../formHelper/delete";
import { all_routes } from "../../../router/backendComponents/admin/all_routes";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import dayjs from "dayjs";
import type { RootState, AppDispatch } from "../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { fetchRolesPermissionsOverview } from "../../../core/data/redux/rolesPermissionsOverview";

const MySwal = withReactContent(Swal);
const showAlertHtml = (html: string) =>
  MySwal.fire({ html, confirmButtonColor: "#c21607", confirmButtonText: "OK" });

interface Permission {
  id: string;
  code: string;
  name: string;
  module: string;
}

interface Module {
  id: number;
  name: string;
  permissions: Permission[];
}

interface Role {
  id: number;
  name: string;
  permissions: { [module: string]: string[] };
  created_at?: string;
}

interface PermissionList {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

const prettify = (str: string) =>
  String(str)
    .split("_")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ");

const backendRoute = all_routes.backend;

const getInitialPerPage = () =>
  typeof window !== "undefined" && window.innerWidth <= 768 ? 5 : 10;

const RoleAccess: React.FC = () => {
  /* ------------------ State ------------------ */
  const [roles, setRoles] = useState<Role[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(getInitialPerPage);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [branchFilter, setBranchFilter] = useState<number[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<number[]>([]);

  const [permissionList, setPermissionList] = useState<
    Record<string, string[]>
  >({});
  const [modules, setModules] = useState<Module[]>([]);
  const [permissionTypes, setPermissionTypes] = useState<string[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [roleName, setRoleName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(
    new Set()
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const rolesAbortRef = useRef<AbortController | null>(null);
  const permAbortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<number | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  /* ------------------ CSS injection (single-file convenience) ------------------ */
  useEffect(() => {
    const css = `
/* --- RoleAccessSingleFile styles --- */
.ra-container { padding: 1rem; max-height: 100vh; overflow-y: auto; }
.ra-perm-wrapper { width: 100%; overflow-x: auto; }
.ra-perm-table { width: 100%; border-collapse: collapse; min-width: 700px; font-size: 14px; }
.ra-perm-table th, .ra-perm-table td { padding: 10px 8px; border: 1px solid #e8e8e8; text-align: left; vertical-align: middle; }
.ra-perm-table th { background: #fafafa; font-weight: 600; }
.col-module { min-width: 200px; font-weight: 700; }
.perm-list { display:flex; flex-direction:column; gap:6px; }
.muted { color: var(--ra-muted, #888); }

/* Mobile cards */
.ra-perm-cards { display:none; }
.perm-card { border:1px solid #e6e6e6; border-radius:8px; margin-bottom:12px; overflow:hidden; background:#fff; }
.perm-card-header { padding:10px; font-weight:700; background:#f7f7f7; }
.perm-card-body { padding:10px; display:flex; flex-direction:column; gap:8px; }
.perm-card-row { display:flex; gap:8px; align-items:flex-start; border-top:1px solid #f0f0f0; padding:8px 0; }
.perm-card-row:first-child{ border-top:none; }
.perm-card-label { min-width:120px; font-weight:600; }
.perm-card-values { flex:1; display:flex; flex-direction:column; gap:6px; }

/* Offcanvas responsive width */
.custom-offcanvas { transition: width 180ms ease; }
@media (max-width: 767.98px) {
  .custom-offcanvas { width: 100% !important; right:0; left:0; }
  .offcanvas-backdrop { display:block; } /* ensure backdrop on mobile */
  .ra-perm-table { display:none; }
  .ra-perm-cards { display:block; }
}
@media (min-width: 768px) {
  .custom-offcanvas { width: 1000px !important; }
}
@media (min-width: 1200px) {
  .custom-offcanvas { width: 1500px !important; }
}

/* Accessibility focus */
.ra-checkbox input[type="checkbox"]:focus + span,
.ra-checkbox input[type="checkbox"]:focus { outline: 2px solid #3b82f6; outline-offset:2px; }

/* Small helpers */
.icon-form .form-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:#6b7280; }
.icon-form input.form-control { padding-left:38px; }
@media (max-width: 576px) {
  .icon-form .form-icon { left:8px; }
  .icon-form input.form-control { padding-left:36px; }
}
`;
    const id = "roleaccess-singlefile-styles";
    if (!document.getElementById(id)) {
      const node = document.createElement("style");
      node.id = id;
      node.innerHTML = css;
      document.head.appendChild(node);
    }
    return () => {
      // keep style alive across component lifecycle — do not remove to avoid FOUC on re-mount
    };
  }, []);

  /* ------------------ Helpers ------------------ */
  const buildModulesFromPermissionList = useCallback(
    (permList: Record<string, string[]>) =>
      Object.entries(permList).map(([moduleName, perms], idx) => ({
        id: idx + 1,
        name: moduleName,
        permissions: perms.map((permCode) => ({
          id: permCode,
          code: permCode,
          name: prettify(permCode),
          module: moduleName,
        })),
      })),
    []
  );

  const extractPermissionTypes = useCallback((mods: Module[]) => {
    const set = new Set<string>();
    mods.forEach((m) =>
      m.permissions.forEach((p) => {
        const [type] = p.code.split("_");
        if (type) set.add(type);
      })
    );
    return Array.from(set).sort();
  }, []);

  const permToModuleMap = useMemo(() => {
    const map = new Map<string, string>();
    Object.entries(permissionList).forEach(([moduleName, perms]) =>
      perms.forEach((perm) => map.set(perm, moduleName))
    );
    return map;
  }, [permissionList]);

  /* ------------------ API ------------------ */
  const fetchPermission = useCallback(async () => {
    if (permAbortRef.current) permAbortRef.current.abort();
    const controller = new AbortController();
    permAbortRef.current = controller;

    setLoading(true);
    try {
      const result = await apiRequestCall(
        "post",
        backendRoute.listPermissions,
        {}
      );
      const res = result?.data?.data;
      if (res && typeof res === "object") {
        setPermissionList(res);
        const mods = buildModulesFromPermissionList(res);
        setModules(mods);
        setPermissionTypes(extractPermissionTypes(mods));
      } else {
        setPermissionList({});
        setModules([]);
        setPermissionTypes([]);
      }
    } catch (e: any) {
      if (e?.name === "AbortError") {
      } else {
        console.error("fetchPermission failed:", e);
        setPermissionList({});
        setModules([]);
        setPermissionTypes([]);
      }
    } finally {
      setLoading(false);
      permAbortRef.current = null;
    }
  }, [buildModulesFromPermissionList, extractPermissionTypes]);

  const fetchRoles = useCallback(
    async ({ page = 1, per_page = perPage } = {}) => {
      if (rolesAbortRef.current) rolesAbortRef.current.abort();
      const controller = new AbortController();
      rolesAbortRef.current = controller;

      setLoading(true);
      try {
        const result = await apiRequestCall(
          "post",
          backendRoute.listRolesWithPermissions,
          {
            data: {
              page,
              per_page,
              start_date: startDate || undefined,
              end_date: endDate || undefined,
              search: query.trim() || undefined,
              branch_list: branchFilter,
              // owner_list: ownerFilter.length ? ownerFilter : undefined,
            },
          }
        );

        const res = result?.data?.data;
        if (res) {
          setRoles(res.data ?? []);
          setCurrentPage(res.current_page ?? page);
          setPerPage(res.per_page ?? per_page);
          setTotalItems(res.total ?? 0);
        } else {
          setRoles([]);
          setTotalItems(0);
        }
      } catch (e: any) {
        if (e?.name === "AbortError") {
        } else {
          console.error("fetchRoles failed:", e);
          setRoles([]);
          setTotalItems(0);
        }
      } finally {
        setLoading(false);
        rolesAbortRef.current = null;
      }
    },
    [branchFilter, endDate, ownerFilter, perPage, query, startDate]
  );

  useEffect(() => {
    fetchPermission();
    fetchRoles({ page: currentPage, per_page: perPage });
    return () => {
      if (permAbortRef.current) permAbortRef.current.abort();
      if (rolesAbortRef.current) rolesAbortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setCurrentPage(1);
      fetchRoles({ page: 1, per_page: perPage });
    }, 400);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [
    query,
    startDate,
    endDate,
    branchFilter,
    ownerFilter,
    perPage,
    fetchRoles,
  ]);

  /* ------------------ Form openers ------------------ */
  const openEditForm = useCallback(
    (role: Role) => {
      setSelectedRoleId(role.id);
      setRoleName(role.name || "");
      const flat = new Set<string>();
      Object.values(role.permissions || {}).forEach((permArray) =>
        permArray.forEach((p) => flat.add(p))
      );
      setSelectedPermissions(flat);
      const merged: Record<string, string[]> = { ...permissionList };
      Object.entries(role.permissions || {}).forEach(([mod, perms]) => {
        if (!merged[mod]) merged[mod] = [];
        perms.forEach((p) => {
          if (!merged[mod].includes(p)) merged[mod].push(p);
        });
      });

      const mods = buildModulesFromPermissionList(merged);
      setModules(mods);
      setPermissionTypes(extractPermissionTypes(mods));
      setShowForm(true);
    },
    [buildModulesFromPermissionList, extractPermissionTypes, permissionList]
  );

  const togglePermission = useCallback((permId: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
  }, []);

  /* ------------------ Submit ------------------ */
  const handleSubmit = useCallback(async () => {
    if (!roleName.trim()) {
      showAlertHtml(
        '<div class="alert alert-primary" role="alert"><strong>Role</strong> name cannot be empty</div>'
      );
      return;
    }

    if (selectedPermissions.size === 0) {
      showAlertHtml(
        '<div class="alert alert-primary" role="alert"><strong>Permission</strong> - select at least one permission</div>'
      );
      return;
    }

    const grouped: { [module: string]: string[] } = {};
    selectedPermissions.forEach((permId) => {
      const moduleName = permToModuleMap.get(permId) ?? "unknown";
      if (!grouped[moduleName]) grouped[moduleName] = [];
      grouped[moduleName].push(permId);
    });

    setSaving(true);
    try {
      const res = await apiRequestCall("post", backendRoute.addUpdateRole, {
        data: {
          id: selectedRoleId,
          name: roleName.trim(),
          permissions: grouped,
        },
      });

      if (res?.data?.code === 200) {
        setShowSuccess(true);
        setShowMessage(res.data.message || "Saved");
        setShowForm(false);
        fetchRoles({ page: currentPage, per_page: perPage });
        setRoleName("");
        setSelectedPermissions(new Set());
        setSelectedRoleId(null);
      } else {
        const msg = res?.data?.message || "Failed to save";
        showAlertHtml(`<div class="alert alert-danger">${msg}</div>`);
      }
      dispatch(fetchRolesPermissionsOverview());
    } catch (e) {
      console.error("save role failed:", e);
      showAlertHtml(
        '<div class="alert alert-danger">Failed to save role. Please try again.</div>'
      );
    } finally {
      setRoleName("");
      setSelectedPermissions(new Set());
      setSaving(false);
    }
  }, [
    currentPage,
    fetchRoles,
    perPage,
    roleName,
    selectedPermissions,
    selectedRoleId,
  ]);

  /* ------------------ Delete ------------------ */
  const deleteRecord = useCallback((id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteId) return;
    setLoading(true);
    try {
      const res = await apiRequestCall("post", backendRoute.deleteRole, {
        data: { id: deleteId },
      });
      if (res?.data?.code === 200) {
        setShowSuccess(true);
        setShowMessage(res.data.message || "Deleted");
        fetchRoles({ page: currentPage, per_page: perPage });
      } else {
        showAlertHtml(
          `<div class="alert alert-danger">${
            res?.data?.message || "Delete failed"
          }</div>`
        );
      }
    } catch (e) {
      console.error("delete failed:", e);
      showAlertHtml('<div class="alert alert-danger">Delete failed.</div>');
    } finally {
      setDeleteId(null);
      setShowDeleteModal(false);
      setLoading(false);
    }
  }, [currentPage, deleteId, fetchRoles, perPage]);

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as PermissionList | null;

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  /* ------------------ Columns ------------------ */
  const columns = useMemo(
    () => [
      {
        key: "id",
        label: "Sr. No",
        render: (_: any, i: number) => (currentPage - 1) * perPage + i + 1,
      },
      { key: "name", label: "Role Name" },
      {
        key: "created_at",
        label: "Created Date",
      },

      ...(permissionMap?.role_access?.edit_role_access ||
      permissionMap?.role_access?.delete_role_access
        ? [
            {
              key: "action",
              label: "Action",
              render: (row: Role) => (
                <div className="hstack gap-2 fs-15">
                  {permissionMap?.role_access?.edit_role_access && (
                    <button
                      className="btn btn-icon btn-sm btn-soft-info"
                      onClick={() => openEditForm(row)}
                      title="Edit Role"
                      aria-label={`Edit role_access ${row.name}`}
                    >
                      <i className="feather-edit" />
                    </button>
                  )}

                  {permissionMap?.role_access?.delete_role_access && (
                    <button
                      className="btn btn-icon btn-sm btn-soft-danger"
                      onClick={() => deleteRecord(row.id)}
                      title="Delete Role"
                      aria-label={`Delete role_access ${row.name}`}
                    >
                      <i className="feather-trash" />
                    </button>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [currentPage, perPage, openEditForm, deleteRecord]
  );

  useEffect(() => {
    if (showForm) {
      document
        .querySelector(".offcanvas.offcanvas-end")
        ?.setAttribute("style", "width:1400px !important; visibility:visible;");
    }
  }, [showForm]);

  /* ------------------ Render ------------------ */
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="row page-header">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h4 className="page-title mb-0">
                  Role & Permissions
                  <span className="count-title">{totalItems}</span>
                </h4>
              </div>

              {permissionMap?.["role_access"]?.["add_role_access"] && (
                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      className="btn btn-soft-success d-flex align-items-center"
                      onClick={() => {
                        setSelectedRoleId(null);
                        setRoleName("");
                        setSelectedPermissions(new Set());
                        const mods =
                          buildModulesFromPermissionList(permissionList);
                        setModules(mods);
                        setPermissionTypes(extractPermissionTypes(mods));
                        setShowForm(true);
                      }}
                      aria-label="Add new role"
                    >
                      <i className="ti ti-square-rounded-plus" />
                      <span className="d-none d-md-inline ms-1">New</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Controls card */}
        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-4">
                <div className="icon-form position-relative">
                  <span className="form-icon">
                    <i className="ti ti-search" />
                  </span>
                  <Search
                    placeholder="Search Role"
                    value={query}
                    onChange={(v) => {
                      setQuery(v);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="icon-form position-relative">
                  <span className="form-icon">
                    <i className="ti ti-calendar" />
                  </span>
                  <DateRangePicker
                    initialSettings={{
                      autoUpdateInput: false,
                      locale: { cancelLabel: "Clear" },
                    }}
                    onCallback={(start, end) => {
                      setStartDate(start.format("YYYY-MM-DD"));
                      setEndDate(end.format("YYYY-MM-DD"));
                      setCurrentPage(1);
                    }}
                    onCancel={() => {
                      setStartDate("");
                      setEndDate("");
                      setCurrentPage(1);
                    }}
                  >
                    <input
                      type="text"
                      className="form-control bookingrange"
                      placeholder="Select Date Range"
                      value={
                        startDate && endDate
                          ? `${dayjs(startDate).format("DD-MM-YYYY")} - ${dayjs(
                              endDate
                            ).format("DD-MM-YYYY")}`
                          : ""
                      }
                      readOnly
                      style={{ cursor: "pointer" }}
                      aria-label="Select date range"
                    />
                  </DateRangePicker>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <FilterDropdown
                  label="Branch"
                  endpoint="/getBranchDD"
                  selected={branchFilter}
                  onChange={(v) => setBranchFilter(v as number[])}
                />
              </div>
              {/* <div className="col-12 col-md-3">
                <FilterDropdown
                  label="Owner"
                  endpoint="/getOwnerDD"
                  selected={ownerFilter}
                  onChange={(v) => setOwnerFilter(v as number[])}
                />
              </div> */}
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              {loading ? (
                <Loader />
              ) : (
                <>
                  <Table data={roles} columns={columns} />
                </>
              )}
            </div>
            <Pagination
              currentPage={currentPage}
              perPage={perPage}
              totalItems={totalItems}
              onPageChange={(p) => {
                setCurrentPage(p);
                fetchRoles({ page: p, per_page: perPage });
              }}
              onPerPageChange={(n) => {
                setPerPage(n);
                setCurrentPage(1);
                fetchRoles({ page: 1, per_page: n });
              }}
              perPageOptions={[
                { value: 5, label: "5" },
                { value: 10, label: "10" },
                { value: 25, label: "25" },
              ]}
            />
          </div>
        </div>

        {/* Offcanvas Form (single-file responsive) */}
        <div
          className={`offcanvas offcanvas-end offcanvas-large custom-offcanvas ${
            showForm ? "show" : ""
          }`}
          style={{ visibility: showForm ? "visible" : "hidden" }}
          tabIndex={-1}
          role="dialog"
          aria-hidden={!showForm}
        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">
              {selectedRoleId ? "Update Role" : "Add Role"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                setShowForm(false);
                setRoleName("");
                setSelectedRoleId(null);
                setSelectedPermissions(new Set());
              }}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="offcanvas-body ra-container">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!saving) handleSubmit();
              }}
            >
              <div className="mb-3">
                <label htmlFor="roleName" className="form-label">
                  Role Name
                </label>
                <input
                  id="roleName"
                  type="text"
                  className="form-control"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="ra-perm-wrapper mb-3">
                <table
                  role="grid"
                  aria-label="Permissions matrix"
                  style={{
                    width: "100%",
                    tableLayout: "auto",
                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr>
                      <th className="" style={{
                            border: "1px solid #dee2e6",
                            padding: "2px",
                            background: "#f8f9fa",
                            textAlign: "left",
                            whiteSpace: "nowrap",
                            width: "100px"
                          }}>Module</th>
                      {permissionTypes.map((type) => (
                        <th
                          style={{
                            border: "1px solid #dee2e6",
                            padding: "2px",
                            background: "#f8f9fa",
                            textAlign: "left",
                            whiteSpace: "nowrap",
                          }}
                          key={type}
                        >
                          <small>{prettify(type)}</small>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((mod) => (
                      <tr key={mod.id}>
                        <td style={{
                            border: "1px solid #dee2e6",
                            padding: "2px",
                            background: "#f8f9fa",
                              textAlign: "left",
                            whiteSpace: "nowrap",
                            fontWeight: 600,
                          }}>
                          <small>{prettify(mod.name)}</small>
                        </td>
                        {permissionTypes.map((type) => {
                          const permsOfType = mod.permissions.filter(
                            (p) => p.code.split("_")[0] === type
                          );
                          return (
                            <td style={{
                              border: "1px solid #dee2e6",
                              padding: "3px",
                              textAlign: "left",
                              fontWeight: 600,
                            }}key={type}>
                              {permsOfType.length > 0 ? (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 6,
                                  }}
                                >
                                  {permsOfType.map((perm) => (
                                    <label
                                      key={perm.id}
                                      className="ra-checkbox"
                                      style={{ margin: 0, fontSize: 13 }}
                                    >
                                      <input
                                        type="checkbox"
                                        id={perm.id}
                                        checked={selectedPermissions.has(
                                          perm.id
                                        )}
                                        onChange={() =>
                                          togglePermission(perm.id)
                                        }
                                        aria-label={`${perm.name} permission for ${mod.name}`}
                                      />
                                      <small>
                                        <span>{perm.name}</span>
                                      </small>
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color: "var(--ra-muted)" }}>
                                  —
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}   
                  </tbody>
                </table>

                {/* Mobile card layout */}
                <div className="ra-perm-cards" aria-hidden={false}>
                  {modules.map((mod) => (
                    <div className="perm-card" key={`card-${mod.id}`}>
                      <div className="perm-card-header">
                        {prettify(mod.name)}
                      </div>
                      <div className="perm-card-body">
                        {permissionTypes.map((type) => {
                          const permsOfType = mod.permissions.filter(
                            (p) => p.code.split("_")[0] === type
                          );
                          return (
                            <div
                              className="perm-card-row"
                              key={`${mod.id}-${type}`}
                            >
                              <div className="perm-card-label">
                                {prettify(type)}
                              </div>
                              <div className="perm-card-values">
                                {permsOfType.length > 0 ? (
                                  permsOfType.map((perm) => (
                                    <label
                                      key={perm.id}
                                      className="ra-checkbox"
                                      style={{ margin: 0, fontSize: 13 }}
                                    >
                                      <input
                                        type="checkbox"
                                        id={`m-${perm.id}`}
                                        checked={selectedPermissions.has(
                                          perm.id
                                        )}
                                        onChange={() =>
                                          togglePermission(perm.id)
                                        }
                                        aria-label={`${perm.name} permission for ${mod.name}`}
                                      />
                                      <span>{perm.name}</span>
                                    </label>
                                  ))
                                ) : (
                                  <span className="muted">—</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-flex justify-content-end mt-3 gap-2">
                <button
                  type="button"
                  className="btn btn-soft-primary"
                  onClick={() => {
                    setShowForm(false);
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-soft-success"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>

        <Success
          id="formatSuccessModal"
          title="Role"
          message={showMessage}
          show={showSuccess}
          onClose={() => setShowSuccess(false)}
        />
        <Delete
          show={showDeleteModal}
          title="Delete Role?"
          message="Are you sure?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default RoleAccess;
